import { useState, useCallback, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { StackProvider, StackTheme, StackHandler, useUser } from "@stackframe/react";
import { stackClientApp } from "./stack";
import "./App.css";
import { weekDays } from "./data/rooms";
import { roomTemplates } from "./data/rooms";
import { zoneColors, zoneLabels } from "./data/floorplan";
import {
  loadState,
  persistState,
  pushRemoteState,
  getResolveRooms,
  getFlagSummary,
} from "./state/roomState";

import Hero from "./components/Hero";
import WhiteboardPanel from "./components/WhiteboardPanel";
import CalendarPanel from "./components/CalendarPanel";
import ResolvePanel from "./components/ResolvePanel";
import AuditWorkspace from "./components/AuditWorkspace";
import Toast from "./components/Toast";
import FloorPlan from "./components/FloorPlan";
import RoomPage from "./components/RoomPage";
import ControlsPage from "./components/ControlsPage";
import LandingPage from "./components/LandingPage";
import NotebookChat from "./components/NotebookChat";

import Sidebar from "./components/Sidebar";
import TopNav from "./components/TopNav";

function Dashboard() {
  const [rooms, setRooms] = useState(() => loadState());
  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [pendingConnectionFrom, setPendingConnectionFrom] = useState(null);
  const [toastMsg, setToastMsg] = useState("");
  const [toastKey, setToastKey] = useState(0);
  const [activeZone, setActiveZone] = useState(null);

  const showToast = useCallback((msg) => {
    setToastMsg(msg);
    setToastKey((k) => k + 1);
  }, []);

  const persist = useCallback(() => {
    persistState(rooms);
    pushRemoteState(rooms);
    setRooms([...rooms]); // trigger re-render
  }, [rooms]);

  const selectedRoom = rooms.find((r) => r.id === selectedRoomId) || null;

  const resolveRooms = getResolveRooms(rooms);
  const flagged = resolveRooms.filter((r) => getFlagSummary(r).failing.length > 0);
  const savedCount = rooms.filter((r) => r.savedAt).length;

  function handleShiftDay(dir) {
    setSelectedDay((d) => (d + dir + weekDays.length) % weekDays.length);
  }

  function handleSelectRoom(id) {
    setSelectedRoomId(id);
    setPendingConnectionFrom(null);
  }

  function handleSaveRoom() {
    if (!selectedRoom) { showToast("Select a room first"); return; }
    selectedRoom.savedAt = new Date().toISOString();
    persist();
    showToast(selectedRoom.id + " saved");
  }

  function handleResetRoom() {
    if (!selectedRoom) { showToast("Select a room first"); return; }
    const template = roomTemplates.find((t) => t.id === selectedRoom.id);
    if (!template) return;
    const idx = rooms.findIndex((r) => r.id === selectedRoom.id);
    rooms[idx] = structuredClone(template);
    setPendingConnectionFrom(null);
    persist();
    showToast(selectedRoom.id + " reset");
  }

  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "#f8fafc" }}>
        <TopNav />
        <div className="app" style={{ flex: 1, overflowY: "auto", padding: "24px", maxWidth: "none", margin: 0 }}>
          {/* Top Legend matching mockup */}
          <div className="dashboard-legend" style={{ display: "flex", gap: "8px", overflowX: "auto", paddingBottom: "24px" }}>
            <button
              className={`legend-chip ${activeZone === null ? "active" : ""}`}
              onClick={() => setActiveZone(null)}
              style={activeZone === null ? { background: "#166534", color: "#ffffff", borderColor: "#166534" } : {}}
            >
              <span style={{ marginRight: 6 }}>⬚</span> All Rooms
            </button>
            {Object.entries(zoneLabels).map(([zone, label]) => {
              const colors = zoneColors[zone];
              return (
                <button
                  key={zone}
                  className={`legend-chip ${activeZone === zone ? "active" : ""}`}
                  style={activeZone === zone ? { background: colors.fill, color: colors.text, borderColor: colors.stroke } : {}}
                  onClick={() => setActiveZone(activeZone === zone ? null : zone)}
                >
                  <span
                    className="legend-dot"
                    style={{ background: colors.stroke, marginRight: 6 }}
                  />
                  {label}
                </button>
              );
            })}
          </div>

          {/* Calendar + facility map (full width; calibration form lives on Controls when needed) */}
          <div
            className="dashboard-2-col"
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(420px, 1.5fr) minmax(400px, 1.5fr)",
              gap: "28px",
              alignItems: "start",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <CalendarPanel
                standalone={true}
                rooms={rooms}
                selectedDay={selectedDay}
                onSelectDay={setSelectedDay}
                onShiftDay={handleShiftDay}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <FloorPlan standalone={true} activeZone={activeZone} />
            </div>
          </div>

          <div className="layout" style={{ marginTop: 24 }}>
            <ResolvePanel
              rooms={rooms}
              selectedRoomId={selectedRoomId}
              onSelectRoom={handleSelectRoom}
            />
          </div>

          <Hero
            resolveCount={resolveRooms.length}
            flaggedCount={flagged.length}
            savedCount={savedCount}
          />

          <WhiteboardPanel />

          <AuditWorkspace
            room={selectedRoom}
            pendingConnectionFrom={pendingConnectionFrom}
            setPendingConnectionFrom={setPendingConnectionFrom}
            onPersist={persist}
            onToast={showToast}
            onSaveRoom={handleSaveRoom}
            onResetRoom={handleResetRoom}
          />
        </div>
      </div>
      <Toast key={toastKey} message={toastMsg} />
    </div>
  );
}

/** Uses Stack’s own redirect (hosted handler when configured), not a React Router path to /handler/sign-in. */
function RequireAuth({ children }) {
  useUser({ or: "redirect" });
  return children;
}

function HandlerRoutes() {
  const location = useLocation();
  return <StackHandler app={stackClientApp} location={location.pathname} fullPage />;
}

function App() {
  return (
    <Suspense fallback={null}>
      <BrowserRouter>
        <StackProvider app={stackClientApp}>
          <StackTheme>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/handler/*" element={<HandlerRoutes />} />
              <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
              <Route path="/room/:roomId" element={<RequireAuth><RoomPage /></RequireAuth>} />
              <Route path="/controls/:roomId" element={<RequireAuth><ControlsPage /></RequireAuth>} />
              <Route path="/notebook-ai" element={
                <RequireAuth>
                  <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>
                    <Sidebar />
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "#f8fafc" }}>
                      <TopNav />
                      <NotebookChat />
                    </div>
                  </div>
                </RequireAuth>
              } />
            </Routes>
          </StackTheme>
        </StackProvider>
      </BrowserRouter>
    </Suspense>
  );
}

export default App;
